// Global Disruptor Pricing page. Public, no login required.
// V2 True Glass with champagne bloom lighting. Champagne rim on the
// featured (Service) tier. Zero em dashes anywhere in the copy.
//
// Data source: GET /api/pricing/tiers (optionally ?region=US to preview
// a different market). The API returns the caller's region automatically
// based on their saved preference, geo IP, or the AU default.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Globe2, ArrowRight, ShieldCheck, Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useMetaTags } from "@/lib/useMetaTags";

// V2 True Glass tier card. Champagne bloom on the featured card.
function TierCard({ tier, cadence, featured }) {
  const price = cadence === "yearly" ? tier.yearly_per_month_display : tier.monthly_display;
  const suffix = cadence === "yearly" ? "per month, billed yearly" : "per month";
  const yearlyTotal = cadence === "yearly" ? tier.yearly_display : null;
  const savings = cadence === "yearly" && tier.yearly_savings > 0
    ? `${tier.currency_symbol}${tier.yearly_savings.toLocaleString()} saved vs monthly`
    : null;

  const bloom = featured
    ? "0 0 90px -18px rgba(232,207,154,0.55), 0 0 40px -14px rgba(232,207,154,0.45)"
    : "0 0 60px -22px rgba(232,207,154,0.28)";

  return (
    <div
      data-testid={`tier-card-${tier.id}`}
      className="relative flex flex-col p-7 md:p-8 h-full"
      style={{
        borderRadius: 26,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.015) 100%)",
        backdropFilter: "blur(44px) saturate(200%)",
        WebkitBackdropFilter: "blur(44px) saturate(200%)",
        border: featured
          ? "1px solid rgba(232,207,154,0.55)"
          : "1px solid rgba(255,255,255,0.14)",
        boxShadow: [
          "inset 0 1px 0 rgba(255,255,255,0.32)",
          "inset 0 -1px 0 rgba(255,255,255,0.04)",
          "0 60px 120px -30px rgba(0,0,0,0.9)",
          "0 30px 60px -14px rgba(0,0,0,0.7)",
          "0 0 0 1px rgba(232,207,154,0.10)",
          bloom,
        ].join(","),
      }}
    >
      {/* Chromatic prism hairline on top */}
      <div
        aria-hidden
        className="absolute top-0 left-6 right-6 h-px rounded-full pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(232,207,154,0.9), rgba(255,255,255,0.6), rgba(90,178,201,0.85), transparent)",
        }}
      />
      {/* Diagonal specular sheen */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius: 26,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, transparent 32%, transparent 70%, rgba(255,255,255,0.05) 100%)",
          mixBlendMode: "screen",
          opacity: 0.65,
        }}
      />
      {featured && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em]"
          style={{
            background: "linear-gradient(180deg, rgba(232,207,154,0.32), rgba(200,169,106,0.14))",
            border: "1px solid rgba(232,207,154,0.55)",
            color: "#faf3df",
            boxShadow: "0 6px 18px -6px rgba(232,207,154,0.55)",
          }}
          data-testid={`tier-badge-${tier.id}`}
        >
          <Sparkles className="w-3 h-3" /> Most Chosen
        </div>
      )}

      <div className="relative z-10 flex flex-col h-full">
        <div className="label-eyebrow gold mb-3">Tier {tier.id === "prep" ? "1" : tier.id === "service" ? "2" : "3"}</div>
        <h3 className="font-display text-3xl md:text-4xl leading-none mb-2" data-testid={`tier-name-${tier.id}`}>
          {tier.name}
        </h3>
        <p className="text-sm text-white/70 leading-relaxed mb-6" data-testid={`tier-tagline-${tier.id}`}>
          {tier.tagline}
        </p>

        <div className="mb-6">
          <div className="flex items-baseline gap-2">
            <div className="font-display text-5xl leading-none tabular" data-testid={`tier-price-${tier.id}`}>
              {price}
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">{tier.currency}</div>
          </div>
          <div className="text-xs text-white/55 mt-2">{suffix}</div>
          {yearlyTotal && (
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 mt-1.5">
              {yearlyTotal} paid yearly {savings ? `: ${savings}` : ""}
            </div>
          )}
        </div>

        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] mb-6 self-start"
          style={{ background: "rgba(232,207,154,0.10)", border: "1px solid rgba(232,207,154,0.28)", color: "#e8cf9a" }}
        >
          {tier.seats}
          {tier.recipe_limit ? ` : ${tier.recipe_limit} recipes` : " : unlimited recipes"}
        </div>

        <ul className="space-y-2.5 mb-8 flex-1">
          {tier.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5" data-testid={`tier-feature-${tier.id}-${i}`}>
              <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#e8cf9a" }} />
              <span className="text-sm text-white/80 leading-snug">{f}</span>
            </li>
          ))}
        </ul>

        <Link
          to={`/register?plan=${tier.id}&cadence=${cadence}`}
          data-testid={`tier-cta-${tier.id}`}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-full text-sm font-medium py-3 transition-transform hover:scale-[1.02]"
          style={
            featured
              ? {
                  background: "linear-gradient(180deg, rgba(232,207,154,0.42), rgba(200,169,106,0.14))",
                  border: "1px solid rgba(232,207,154,0.65)",
                  color: "#0a0806",
                  fontWeight: 600,
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.32), 0 10px 28px -8px rgba(232,207,154,0.45)",
                }
              : {
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(232,207,154,0.32)",
                  color: "#faf3df",
                }
          }
        >
          {tier.cta} <ArrowRight className="w-4 h-4" />
        </Link>
        <div className="text-[10px] text-white/40 text-center mt-2">14 day free trial. No card required.</div>
      </div>
    </div>
  );
}

function RegionSelector({ region, available, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid="pricing-region-picker">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em]"
        style={{ background: "rgba(232,207,154,0.10)", border: "1px solid rgba(232,207,154,0.35)", color: "#e8cf9a" }}
        data-testid="pricing-region-toggle"
      >
        <Globe2 className="w-3.5 h-3.5" />
        {region.label} : {region.currency}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-20 w-64 rounded-xl overflow-hidden"
          style={{
            background: "rgba(10,8,6,0.96)",
            border: "1px solid rgba(232,207,154,0.35)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
          }}
          data-testid="pricing-region-menu"
        >
          <div className="max-h-72 overflow-y-auto">
            {available.map((r) => (
              <button
                key={r.code}
                onClick={() => {
                  onChange(r.code);
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 text-sm text-white/85 hover:bg-white/5 flex items-center justify-between"
                data-testid={`pricing-region-option-${r.code}`}
              >
                <span>{r.label}</span>
                <span className="text-[10px] text-white/45">{r.symbol}{r.currency}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Pricing() {
  useMetaTags({
    title: "Pricing : Culinaire Specialist",
    description:
      "The precision cost engine for professional kitchens. Three tiers. Global pricing. Fourteen day free trial on every plan.",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cadence, setCadence] = useState("monthly");

  const load = async (regionCode = null) => {
    setLoading(true);
    try {
      const { data } = await api.get("/pricing/tiers", {
        params: regionCode ? { region: regionCode } : {},
      });
      setData(data);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const tiers = data?.tiers || [];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#0a0806" }} data-testid="pricing-page">
      {/* Ambient champagne bloom in the background so the cards feel lit */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1200px 700px at 50% -10%, rgba(232,207,154,0.16), transparent 60%), radial-gradient(900px 900px at 20% 110%, rgba(90,178,201,0.08), transparent 70%)",
        }}
      />

      <header className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-4 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="pricing-home-link">
          <span className="w-8 h-8 rounded-full bg-[#e8cf9a]/15 border border-[#e8cf9a]/40 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#e8cf9a]" />
          </span>
          <span className="font-display text-lg text-white/90">Culinaire Specialist</span>
        </Link>
        {data?.region && (
          <RegionSelector
            region={data.region}
            available={data.available_regions || []}
            onChange={(code) => load(code)}
          />
        )}
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pt-6 pb-8 text-center">
        <div className="label-eyebrow gold mb-3 inline-block">Global Disruptor Pricing</div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-4">
          Three tiers. One math engine.
        </h1>
        <p className="text-sm md:text-base text-white/70 max-w-2xl mx-auto leading-relaxed">
          Priced against your local market, not the US baseline. Every currency is set intentionally so a Manila chef pays a Manila price and a New York chef pays a New York price. Fair by design.
        </p>

        <div
          className="inline-flex items-center gap-1 mt-8 p-1 rounded-full"
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(232,207,154,0.24)" }}
          data-testid="cadence-toggle"
        >
          {["monthly", "yearly"].map((c) => (
            <button
              key={c}
              onClick={() => setCadence(c)}
              data-testid={`cadence-${c}`}
              className="px-5 py-2 rounded-full text-xs uppercase tracking-[0.18em] transition-colors"
              style={
                cadence === c
                  ? {
                      background: "linear-gradient(180deg, rgba(232,207,154,0.32), rgba(200,169,106,0.10))",
                      border: "1px solid rgba(232,207,154,0.5)",
                      color: "#faf3df",
                    }
                  : { color: "rgba(255,255,255,0.55)" }
              }
            >
              {c === "monthly" ? "Monthly" : "Yearly (2 months free)"}
            </button>
          ))}
        </div>
      </section>

      {loading && (
        <div className="flex justify-center py-24" data-testid="pricing-loading">
          <Loader2 className="w-6 h-6 animate-spin text-white/60" />
        </div>
      )}

      {!loading && tiers.length > 0 && (
        <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {tiers.map((tier) => (
              <TierCard key={tier.id} tier={tier} cadence={cadence} featured={!!tier.featured} />
            ))}
          </div>
          <div className="text-center mt-10 text-xs text-white/45 leading-relaxed max-w-2xl mx-auto">
            Every tier includes the 14 day free trial. No card required at signup. Cancel any time from the billing page. All prices exclude local tax where applicable.
          </div>
        </section>
      )}

      <footer className="relative z-10 border-t border-white/5 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-between gap-4 text-xs text-white/45">
          <div>© 2026 Culinaire Specialist</div>
          <div className="flex items-center gap-5">
            <Link to="/trust" className="hover:text-white/80 transition-colors inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Trust
            </Link>
            <Link to="/blog" className="hover:text-white/80 transition-colors">Journal</Link>
            <Link to="/register" className="hover:text-white/80 transition-colors">Create account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
